import * as preact from "preact";
import { useEffect, useState } from "preact/hooks";

import * as buttonRole from "ui/buttonRole";
import Menu from "ui/Menu";
import * as userMenu from "userMenu";
import HeaderLanguagePicker from "views/HeaderLanguagePicker";
import NavigationIcon from "views/NavigationIcon";
import StreakView from "views/StreakView";

type Props = {
  activeLanguages?: string[];
  children?: preact.ComponentChildren;
  flashcardHintReadyToStartCount?: number;
  flashcardHintVisible?: boolean;
  /**
   * The iOS app draws the header natively instead (nativeHeader.ts), and
   * leaves out the footer's site links
   */
  nativeHeader?: boolean;
  onAccountClick: () => void;
  onAdminClick: () => void;
  onDismissFlashcardHint: () => void;
  onFirstLanguageSelect: (languageCode: string) => void;
  onForumClick: () => void;
  onLearningLanguageSelect: (languageCode: string) => void;
  onLogoClick: () => void;
  onLogoutClick: () => void;
  onPracticeClick: () => void;
  onProfileClick: () => void;
  onSettingsClick: () => void;
  onSignInClick: () => void;
  onUserPasswordClick: () => void;
  onWordsClick: () => void;
  showAdminPanel?: boolean;
  showStreak?: boolean;
  showWebReaderInstaller?: boolean;
  streakStore: any;
  user: any;
};

const handleLinkClick = (
  event: preact.JSX.TargetedMouseEvent<HTMLElement>,
  callback: () => void
) => {
  event.preventDefault();
  event.stopPropagation();
  callback();
};

const MainLayoutView = ({
  activeLanguages,
  children,
  flashcardHintReadyToStartCount,
  flashcardHintVisible,
  nativeHeader,
  onAccountClick,
  onAdminClick,
  onDismissFlashcardHint,
  onFirstLanguageSelect,
  onForumClick,
  onLearningLanguageSelect,
  onLogoClick,
  onLogoutClick,
  onPracticeClick,
  onProfileClick,
  onSettingsClick,
  onSignInClick,
  onUserPasswordClick,
  onWordsClick,
  showAdminPanel,
  showStreak,
  showWebReaderInstaller,
  streakStore,
  user,
}: Props) => {
  const menuProps = {
    onAccountClick,
    onAdminClick,
    onForumClick,
    onLogoutClick,
    onProfileClick,
    onSettingsClick,
    onUserPasswordClick,
    showAdminPanel,
    user,
  };
  const [rabbitShareVisible, setRabbitShareVisible] = useState(false);

  useEffect(() => {
    if (!rabbitShareVisible) {
      return;
    }

    const dismissRabbitShare = () => setRabbitShareVisible(false);
    const timeoutID = window.setTimeout(() => {
      document.addEventListener("click", dismissRabbitShare);
    }, 0);

    return () => {
      window.clearTimeout(timeoutID);
      document.removeEventListener("click", dismissRabbitShare);
    };
  }, [rabbitShareVisible]);

  const toggleRabbitShare = (
    event: preact.JSX.TargetedMouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    setRabbitShareVisible((visible) => {
      if (!visible) {
        onLogoClick();
      }
      return !visible;
    });
  };

  return (
    <>
      {!nativeHeader && (
        <>
          {user.trialMode && (
            <div id="trialModeNotification">
              You're in trial mode,{" "}
              <a
                href="#"
                class="clickable"
                onClick={(event) => handleLinkClick(event, onSignInClick)}
              >
                Sign In
              </a>{" "}
              to save your progress
            </div>
          )}
          <div id="header">
            <div class="texture"></div>
            <div class="bookColumn">
              <img
                alt=""
                class="ajaxIndicator"
                src="/content/bigGreenThrobber.gif?v2"
              />
              <div id="logo">
                <span
                  class="readlangLogoContainer clickable"
                  onClick={toggleRabbitShare}
                >
                  <img
                    alt=""
                    class="rabbitHole"
                    src="/content/rabbitHole.svg"
                  />
                  <div class="rabbitContainer">
                    <img alt="" class="rabbit" src="/content/rabbit.svg" />
                  </div>
                  <img
                    alt=""
                    class="rabbitHoleForeground"
                    src="/content/rabbitHoleForeground.svg"
                  />
                </span>
                <div
                  class="rabbitShare tooltip arrowLeft"
                  style={{
                    display: rabbitShareVisible ? "block" : "none",
                    left: "50px",
                    padding: "12px 12px 12px 25px",
                    top: "-20px",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div
                    class="closeDialogButton"
                    onClick={() => setRabbitShareVisible(false)}
                  >
                    &nbsp;x&nbsp;
                  </div>
                  <h3>Please share Readlang</h3>
                  <p>
                    I'm too shy to promote Readlang myself, so if you'd tell
                    just one friend about it, that would be great! Thanks :)
                  </p>
                  <p>
                    Here's a link you can copy:{" "}
                    <a href="https://readlang.com">https://readlang.com</a>
                  </p>
                  <p>PS: I love carrots.</p>
                </div>
                <a href="/">
                  <span class="readlangLogoText">Readlang</span>
                </a>
              </div>
              <div id="headerSettings">
                {user.firstLanguageString && (
                  <span class="languageContainer">
                    I know <br class="tabletAndMobileOnly" />
                    <HeaderLanguagePicker
                      id="firstLanguageDropdown"
                      label={user.firstLanguageString}
                      title="Your first language"
                      languageType="first"
                      currentLanguage={user.firstLanguage}
                      onSelect={onFirstLanguageSelect}
                    />
                  </span>
                )}
                {user.currentLearningLanguageString && (
                  <span class="languageContainer">
                    I'm learning <br class="tabletAndMobileOnly" />
                    <HeaderLanguagePicker
                      id="editionDropdown"
                      label={user.currentLearningLanguageString}
                      title="Language you're learning"
                      languageType="learning"
                      currentLanguage={user.currentLearningLanguage}
                      featuredLanguages={activeLanguages}
                      onSelect={onLearningLanguageSelect}
                    />
                  </span>
                )}
                <div id="userInfo" class="dropdownBox">
                  {user.trialMode ? (
                    <button
                      id="signUpButton"
                      data-testid="open-signup-button"
                      class="clickable bright hoverGlow"
                      onClick={(event) => handleLinkClick(event, onSignInClick)}
                    >
                      Sign In
                    </button>
                  ) : (
                    <Menu
                      class="userMenu"
                      sections={[
                        {
                          title: user.displayName,
                          items: userMenu
                            .items(menuProps, { app: false })
                            .map((item) => ({
                              label: item.title,
                              icon: item.icon,
                              onSelect: item.onClick,
                            })),
                        },
                        {
                          items: [
                            {
                              label: userMenu.logout(menuProps).title,
                              onSelect: onLogoutClick,
                            },
                          ],
                        },
                      ]}
                      renderButton={(buttonProps) => (
                        <span
                          {...buttonProps}
                          {...buttonRole.props}
                          id="userDropdown"
                          class="dropdownTitle clickable"
                          aria-label="Account"
                        >
                          <img alt="" class="avatar" src={user.imageURL} />
                          <span class="dropdownArrow"></span>
                        </span>
                      )}
                    />
                  )}
                </div>
                <div id="streak">
                  {showStreak && <StreakView store={streakStore} />}
                </div>
              </div>
              <div class="clear"></div>
              <div id="navBar">
                <ul>
                  <li>
                    <a id="navDashboard" href="/dashboard">
                      <NavigationIcon name="home" /> Home
                    </a>
                  </li>
                  <li>
                    <a id="navLibrary" href="/myTexts">
                      <NavigationIcon name="library" /> Library
                    </a>
                  </li>
                  <li>
                    <a id="navDiscover" href="/library">
                      <NavigationIcon name="discover" /> Discover
                    </a>
                  </li>
                  <li>
                    <a
                      id="navWords"
                      href="/words"
                      class="clickable"
                      onClick={(event) => handleLinkClick(event, onWordsClick)}
                    >
                      <NavigationIcon name="words" /> Words
                    </a>
                  </li>
                  <li class="navTabTest" style={{ position: "relative" }}>
                    <a
                      id="navTest"
                      href="/learn"
                      class={`clickable ${flashcardHintVisible ? "glow" : ""}`}
                      onClick={(event) =>
                        handleLinkClick(event, onPracticeClick)
                      }
                    >
                      <NavigationIcon name="practice" /> Practice
                    </a>
                    <div
                      class="flashcards tooltip arrowLeft"
                      style={{
                        display: flashcardHintVisible ? "block" : "none",
                        padding: "12px 12px 12px 25px",
                      }}
                    >
                      <div
                        class="closeDialogButton dismissFlashcardHint"
                        onClick={onDismissFlashcardHint}
                      >
                        &nbsp;x&nbsp;
                      </div>
                      <h3>Start practising!</h3>
                      <p>
                        We generated{" "}
                        <span class="tooltip-readyToStartCount">
                          {flashcardHintReadyToStartCount}
                        </span>{" "}
                        flashcards for you while you were reading.
                      </p>
                    </div>
                  </li>
                  <div
                    class={`webReaderInstallerContainer ${
                      showWebReaderInstaller ? "active" : ""
                    }`}
                  >
                    <a href="/webReader" class="webReaderLink">
                      Get Web Reader
                    </a>
                  </div>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
      <div class="clear"></div>
      <div class="bookColumn">
        <div id="mainContent">
          {children}
          <div class="clear"></div>
        </div>
      </div>
      {!nativeHeader && (
        <div id="footer" style={{ display: "block" }}>
          <div class="bookColumn">
            <div class="row">
              <div id="socialLinks">
                <p>Connect with us</p>
                <ul>
                  <li>
                    <a href="https://www.facebook.com/readlang" class="logo">
                      <i class="icon-facebook-sign"></i>
                    </a>
                    <a href="https://twitter.com/Readlang" class="logo">
                      {"\uD835\uDD4F"}
                    </a>
                  </li>
                </ul>
              </div>
              <div class="col2_3">
                <ul>
                  <li>
                    <a href="https://forum.readlang.com/">
                      Forum (Help, Feedback & Community)
                    </a>
                  </li>
                  <li>
                    <a
                      href={
                        user.trialMode
                          ? "https://www.youtube.com/watch?v=5vl6Q695VnU&list=PLIGa-eWCssj_dcE9GXkpLSFB9t8qZyZn2&index=1"
                          : "https://www.youtube.com/watch?v=wF31VqZMqE8&index=2&list=PLIGa-eWCssj_dcE9GXkpLSFB9t8qZyZn2"
                      }
                    >
                      Tutorial Videos
                    </a>
                  </li>
                  <li>
                    <a href="https://blog.readlang.com">Readlang Blog</a>
                  </li>
                  <li>
                    <a href="/webReader">Web Reader Extension & Bookmarklet</a>
                  </li>
                  {user.currentLearningLanguage && (
                    <li>
                      <a href={`/${user.currentLearningLanguage}/links`}>
                        Popular {user.currentLearningLanguageEnglishString}{" "}
                        Websites
                      </a>
                    </li>
                  )}
                  <li>
                    <a href="https://forum.readlang.com/t/install-readlang-on-your-iphone-ipad-or-android-home-screen/45">
                      Readlang for mobile
                    </a>
                  </li>
                  <li>
                    <a data-bypass="true" href="/landing-page">
                      Landing Page
                    </a>
                  </li>
                  <li>
                    <a href="/languages">Supported Languages</a>
                  </li>
                  <li>
                    <a data-bypass="true" href="/privacyPolicy">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a data-bypass="true" href="/termsOfService">
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a href="/account">Pricing</a>
                  </li>
                  <li>
                    <a href="/testimonials" data-bypass>
                      User Testimonials
                    </a>
                  </li>
                  <li>
                    <a href="/about" data-bypass>
                      About & Contact
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MainLayoutView;
